(function () {
  'use strict';
  if (customElements.get('eyesnug-mystery-offer')) return;
  const VALID_OUTCOMES = ['gbp10', 'pct20'];
  const INVITATION_DISMISS_MS = 60 * 60 * 1000;
  const CONSENT_SELECTOR = '#shopify-pc__banner, #shopify-pc__prefs';
  class EyesnugMysteryOffer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('[data-mystery-dialog]');
      if (!this.dialog) return;
      this.memory = new Map();
      this.listeners = new AbortController();
      this.storageKey = `eyesnug-mystery-offer-v1:${this.dataset.campaign || 'welcome'}`;
      this.cookieKey = `eyesnug_mystery_offer_v1_${(this.dataset.campaign || 'welcome').replace(/[^a-z0-9_-]/gi, '_').slice(0, 48)}`;
      this.form = this.querySelector('form');
      this.email = this.querySelector('[data-mystery-email]');
      this.tab = this.querySelector('[data-mystery-tab]');
      this.invitation = document.querySelector('[data-mystery-invitation]');
      this.heading = this.querySelector('[data-mystery-heading]');
      this.lastFocused = null;
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundVisibilityChange = this.updateSplashTimer.bind(this);
      const listen = (element, type, callback) => element && element.addEventListener(type, callback, { signal: this.listeners.signal });
      listen(window.visualViewport, 'resize', () => this.fitViewport());
      listen(window.visualViewport, 'scroll', () => this.fitViewport());
      listen(window, 'resize', () => this.fitViewport());
      listen(document, 'eyesnug:cart-priority', event => {
        if (event.detail.active) this.suspendForCart();
        else this.syncConsentPriority();
      });
      listen(document, 'visitorConsentCollected', () => this.syncConsentPriority());
      this.watchConsent();
      listen(this.querySelector('[data-mystery-close]'), 'click', () => this.close());
      listen(this.querySelector('[data-mystery-decline]'), 'click', () => this.close());
      this.querySelectorAll('[data-mystery-box]').forEach(button => listen(button, 'click', () => this.reveal()));
      listen(this.querySelector('[data-copy-code]'), 'click', event => this.copyCode(event.currentTarget));
      this.form?.addEventListener('submit', event => this.prepareSubmission(event), { capture: true, signal: this.listeners.signal });
      listen(this.email, 'input', () => this.syncEmail());
      listen(this.email, 'change', () => this.syncEmail());
      listen(this.form, 'formdata', event => {
        if (this.email) event.formData.set('contact[email]', this.email.value.trim());
      });
      listen(this.querySelector('[data-result-link]'), 'click', event => this.applyDiscount(event));
      listen(this.querySelector('[data-mystery-tab-open]'), 'click', () => this.open());
      listen(this.querySelector('[data-mystery-tab-close]'), 'click', () => this.dismissTab());
      if (this.invitation) {
        listen(this.invitation.querySelector('[data-mystery-open]'), 'click', () => this.open());
        listen(this.invitation.querySelector('[data-mystery-invitation-close]'), 'click', () => this.dismissInvitation());
      }
      const server = this.querySelector('[data-mystery-server-state]');
      const submittedAt = Number(this.read('submitted') || 0);
      const ownSubmission = submittedAt > Date.now() - 3600000 && !!this.pendingOutcome();
      if (server && server.dataset.postedSuccess === 'true' && ownSubmission) {
        this.completeClaim();
        this.open();
        return;
      }
      if (server && server.dataset.formError === 'true' && ownSubmission) {
        this.setState('claim');
        this.open();
        return;
      }
      this.setState(this.completed() && this.pendingOutcome() ? 'success' : this.pendingOutcome() ? 'claim' : 'choose');
      if (this.dataset.designMode === 'true') return;
      if (this.completed()) {
        // Resume a just-claimed offer if navigation interrupted its cart request.
        if (this.pendingOutcome() && Number(this.read('cart-apply-pending') || 0) > Date.now() - 3600000) this.applyDiscount();
        return;
      }
      const stage = this.promptStage();
      if (stage === 'splash') {
        this.startSplashTimer();
      } else if (stage === 'tab') {
        this.showTab();
      } else if (!this.pageSuppressed && this.invitation && this.invitationDismissalRemaining() === 0) {
        // Eligibility is decided on page load; an expiring cooldown never starts a timer.
        const seconds = Math.max(0, Number(this.invitation.dataset.inviteDelay || 10));
        this.scheduleInvitation(seconds * 1000);
      }
    }

    disconnectedCallback() {
      window.EyesnugCartPriority?.cancel(this);
      window.EyesnugCartPriority?.cancel(this.tab);
      window.EyesnugCartPriority?.cancel(this.invitation);
      this.listeners?.abort();
      this.consentObserver?.disconnect();
      this.resumeAfterConsent = false;
      this.invitationWaitingForConsent = false;
      this.discountRequest?.abort();
      window.clearTimeout(this.claimTimer);
      this.claimRequest?.abort();
      this.stopTriggers();
      document.removeEventListener('keydown', this.boundKeydown);
      this.restoreBackground();
      document.documentElement.classList.remove('eyesnug-mystery-offer-open');
      this.initialized = false;
    }
    completed() { return this.read('completed') === '1'; }
    hasSeenSplash() {
      return this.read('seen') === '1' || !!this.read('pending') || !!this.read('dismissed');
    }
    promptStage() {
      const stored = this.read('prompt-stage');
      if (stored === 'tab' || stored === 'notification') return stored;
      // Migrate existing visitors without resetting their offer or remaining cooldown.
      const stage = Number(this.read('invite-dismissed')) > 0 ? 'notification' : this.hasSeenSplash() ? 'tab' : 'splash';
      if (stage !== 'splash') this.write('prompt-stage', stage, 365);
      return stage;
    }

    hidePrompts() {
      if (this.tab) this.tab.hidden = true;
      if (this.invitation) this.invitation.hidden = true;
    }
    showTab() {
      if (window.EyesnugCartPriority?.defer(this.tab, () => this.isConnected && this.showTab())) return;
      if (!this.tab || this.completed() || !this.dialog.hidden || this.pageSuppressed) return;
      if (this.hasVisibleConsent()) return;
      this.hidePrompts();
      this.tab.hidden = false;
    }
    dismissTab() {
      this.stopTriggers();
      this.hidePrompts();
      this.pageSuppressed = true;
      this.write('prompt-stage', 'notification', 365);
    }

    startSplashTimer() {
      const seconds = Number(this.dataset.openDelay || 10);
      this.splashRemaining = (Number.isFinite(seconds) ? Math.max(0, seconds) : 10) * 1000;
      this.splashStartedAt = null;
      document.addEventListener('visibilitychange', this.boundVisibilityChange);
      this.updateSplashTimer();
    }
    updateSplashTimer() {
      if (this.splashStartedAt !== null) {
        this.splashRemaining = Math.max(0, this.splashRemaining - (Date.now() - this.splashStartedAt));
      }
      window.clearTimeout(this.splashTimer);
      window.clearTimeout(this.splashImageTimer);
      this.splashStartedAt = null;
      if (document.hidden || this.hasVisibleConsent() || this.completed() || this.promptStage() !== 'splash' || !this.dialog.hidden) return;
      this.splashStartedAt = Date.now();
      // Prepare the artwork shortly before opening without loading it at page arrival.
      this.splashImageTimer = window.setTimeout(() => this.primeImage(), Math.max(0, this.splashRemaining - 2000));
      this.splashTimer = window.setTimeout(() => {
        this.splashStartedAt = null;
        this.splashRemaining = 0;
        if (!document.hidden && !this.completed() && this.promptStage() === 'splash') this.open();
      }, this.splashRemaining);
    }
    scheduleInvitation(delay) {
      window.clearTimeout(this.inviteTimer);
      this.inviteTimer = window.setTimeout(() => this.showInvitation(), delay);
    }

    showInvitation() {
      if (window.EyesnugCartPriority?.defer(this.invitation, () => this.isConnected && this.showInvitation())) return;
      if (!this.invitation || this.completed() || !this.dialog.hidden || this.pageSuppressed || this.promptStage() !== 'notification' || this.invitationDismissalRemaining() > 0) return;
      if (this.hasVisibleConsent()) {
        this.invitationWaitingForConsent = true;
        return;
      }
      this.invitationWaitingForConsent = false;
      this.hidePrompts();
      this.invitation.hidden = false;
      this.primeImage();
    }

    invitationDismissalRemaining() {
      const dismissedAt = Number(this.read('invite-dismissed') || 0);
      return Math.max(0, dismissedAt + INVITATION_DISMISS_MS - Date.now());
    }

    dismissInvitation() {
      if (!this.invitation) return;
      this.stopTriggers();
      this.hidePrompts();
      this.pageSuppressed = true;
      this.write('prompt-stage', 'notification', 365);
      this.write('invite-dismissed', String(Date.now()), INVITATION_DISMISS_MS / 86400000);
    }

    primeImage() {
      const image = this.querySelector('.eyesnug-mystery-offer__image');
      if (image) image.loading = 'eager';
    }
    stopTriggers() {
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      window.clearTimeout(this.splashTimer);
      window.clearTimeout(this.splashImageTimer);
      this.splashStartedAt = null;
      window.clearTimeout(this.inviteTimer);
    }
    hasVisibleConsent() {
      return [...document.querySelectorAll(CONSENT_SELECTOR)].some(element => {
        const style = window.getComputedStyle(element);
        return element.getClientRects().length > 0 && style.visibility !== 'hidden' && style.visibility !== 'collapse';
      });
    }
    watchConsent() {
      this.consentActive = this.hasVisibleConsent();
      // Consent can load after our timer fires, or preferences can reopen later.
      this.consentObserver = new MutationObserver(records => {
        if (records.some(record => record.type === 'attributes'
          ? record.target.matches?.(CONSENT_SELECTOR) || record.target.closest?.(CONSENT_SELECTOR) || record.target.querySelector?.(CONSENT_SELECTOR)
          : [...record.addedNodes, ...record.removedNodes].some(node => node.matches?.(CONSENT_SELECTOR) || node.querySelector?.(CONSENT_SELECTOR)))) {
          this.syncConsentPriority();
        }
      });
      this.consentObserver.observe(document.body, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'data-eyesnug-cart-priority']
      });
    }
    syncConsentPriority() {
      const active = this.hasVisibleConsent();
      if (active === this.consentActive) return;
      this.consentActive = active;
      if (active) {
        if (this.invitation && !this.invitation.hidden) this.invitationWaitingForConsent = true;
        this.hidePrompts();
        if (!this.dialog.hidden) {
          this.resumeAfterConsent = true;
          this.suspendDialog();
          // Release our focus trap and inert background before focusing consent.
          const panel = [...document.querySelectorAll(CONSENT_SELECTOR)].find(element => element.getClientRects().length && window.getComputedStyle(element).visibility !== 'hidden');
          panel?.querySelector('button, [href], input')?.focus({ preventScroll: true });
        }
        if (Number.isFinite(this.splashRemaining)) this.updateSplashTimer();
        return;
      }
      if (this.resumeAfterConsent) {
        this.resumeAfterConsent = false;
        this.open();
      } else if (this.promptStage() === 'splash' && Number.isFinite(this.splashRemaining)) {
        this.updateSplashTimer();
      } else if (this.promptStage() === 'tab') {
        this.showTab();
      } else if (this.invitationWaitingForConsent) {
        this.showInvitation();
      }
    }
    suspendDialog() {
      this.dialog.hidden = true;
      if (this.email) this.email.disabled = true;
      document.documentElement.classList.remove('eyesnug-mystery-offer-open');
      document.removeEventListener('keydown', this.boundKeydown);
      this.restoreBackground();
    }
    suspendForCart() {
      if (!this.dialog.hidden) {
        this.suspendDialog();
        window.EyesnugCartPriority?.defer(this, () => this.isConnected && this.open());
      }
    }
    open() {
      if (window.EyesnugCartPriority?.defer(this, () => this.isConnected && this.open())) return;
      if (!this.dialog.hidden) return;
      if (this.hasVisibleConsent()) {
        this.resumeAfterConsent = true;
        this.hidePrompts();
        return;
      }
      this.stopTriggers();
      this.lastFocused = document.activeElement;
      this.write('seen', '1', 365);
      this.primeImage();
      this.dialog.hidden = false;
      this.fitViewport();
      if (this.email) this.email.disabled = this.state !== 'claim';
      this.hidePrompts();
      document.documentElement.classList.add('eyesnug-mystery-offer-open');
      // Inert siblings at each ancestor level, preserving the dialog's own branch.
      this.inertElements = [];
      let branch = this;
      while (branch && branch !== document.body) {
        for (const sibling of branch.parentElement?.children || []) {
          if (sibling === branch || sibling.inert || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) continue;
          sibling.inert = true;
          this.inertElements.push(sibling);
        }
        branch = branch.parentElement;
      }
      document.addEventListener('keydown', this.boundKeydown);
      window.requestAnimationFrame(() => {
        if (this.dialog.hidden) return;
        const target = this.state === 'claim' && this.heading ? this.heading : this.dialog;
        target.focus({ preventScroll: true });
      });
    }
    restoreBackground() {
      (this.inertElements || []).forEach(element => { element.inert = false; });
      this.inertElements = [];
    }
    fitViewport() {
      if (this.dialog.hidden) return;
      const viewport = window.visualViewport;
      const height = viewport && viewport.scale === 1 ? viewport.height : window.innerHeight;
      this.dialog.style.setProperty('--offer-height', `${height}px`);
      this.dialog.style.setProperty('--offer-top', `${viewport && viewport.scale === 1 ? viewport.offsetTop : 0}px`);
      this.dialog.dataset.compact = height < 500 ? 'true' : 'false';
    }
    close() {
      this.resumeAfterConsent = false;
      window.EyesnugCartPriority?.cancel(this);
      if (!this.automaticDiscountRequest) this.discountRequest?.abort();
      this.dialog.hidden = true;
      if (this.email) this.email.disabled = true;
      document.documentElement.classList.remove('eyesnug-mystery-offer-open');
      document.removeEventListener('keydown', this.boundKeydown);
      this.restoreBackground();
      this.stopTriggers();
      if (!this.completed()) {
        this.pageSuppressed = false;
        this.write('prompt-stage', 'tab', 365);
        this.showTab();
      }
      const focusTarget = this.lastFocused && this.lastFocused !== document.body && !this.dialog.contains(this.lastFocused) && this.lastFocused.isConnected && this.lastFocused.getClientRects().length
        ? this.lastFocused : this.tab && !this.tab.hidden ? this.tab.querySelector('[data-mystery-tab-open]') : document.querySelector('main');
      focusTarget?.focus({ preventScroll: true });
    }
    onKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab') return;
      const items = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex="0"]')]
        .filter(element => element.getClientRects().length && !element.closest('[hidden]'));
      const first = items[0]; const last = items[items.length - 1];
      if (!first) { event.preventDefault(); this.dialog.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    }
    pendingOutcome() {
      const outcome = this.read('pending');
      if (!VALID_OUTCOMES.includes(outcome)) return null;
      // Keep a revealed offer fixed. A GBP offer is eligible only in the UK.
      if (this.dataset.offerCountry !== 'GB' && outcome === 'gbp10') {
        this.write('pending', 'pct20', 30);
        return 'pct20';
      }
      return outcome;
    }
    chooseOutcome() {
      const existing = this.pendingOutcome();
      if (existing) return existing;
      const outcome = this.dataset.offerCountry === 'GB' && Math.random() < 0.5 ? 'gbp10' : 'pct20';
      this.write('pending', outcome, 30);
      return outcome;
    }
    config() {
      return (this.pendingOutcome() || this.chooseOutcome()) === 'gbp10'
        ? { label: '£10 off', code: this.dataset.gbp10Code, tag: 'eyesnug_offer_gbp10' }
        : { label: '20% off', code: this.dataset.pct20Code, tag: 'eyesnug_offer_pct20' };
    }
    reveal() {
      this.chooseOutcome();
      this.setState('claim');
      // Announce the revealed step without opening the on-screen keyboard.
      (this.heading || this.dialog).focus({ preventScroll: true });
      this.querySelector('[data-mystery-status]').textContent = `Your offer is ${this.config().label} your first order. Enter your email to claim it.`;
    }
    setState(state) {
      const changed = this.state && this.state !== state;
      this.state = state;
      this.dialog.dataset.state = state;
      this.querySelector('[data-mystery-choices]').hidden = state !== 'choose';
      this.querySelector('[data-mystery-revealed]').hidden = state === 'choose';
      this.querySelector('[data-mystery-claim]').hidden = state !== 'claim';
      this.querySelector('[data-mystery-result]').hidden = state !== 'success';
      this.querySelector('[data-mystery-eyebrow]').hidden = state !== 'success';
      this.querySelector('[data-mystery-decline]').hidden = state === 'success';
      if (this.email) this.email.disabled = state !== 'claim' || this.dialog.hidden;
      this.heading.textContent = state === 'choose' ? this.dataset.heading : state === 'claim' ? "It's yours." : this.config().label;
      // Keep the reference's two-line international headline without accepting HTML from settings.
      if (state === 'choose' && this.dataset.offerCountry !== 'GB' && this.dataset.heading === 'Unlock 20% off') {
        this.heading.textContent = this.dataset.offerCountry === 'US' ? 'Unlock up to ' : 'Unlock ';
        const amount = document.createElement('span');
        amount.className = 'eyesnug-mystery-offer__headline-offer';
        amount.textContent = '20% off';
        this.heading.append(amount);
      }
      const lead = this.querySelector('[data-mystery-lead]');
      lead.textContent = state === 'choose' ? this.dataset.description : 'Enter your email to claim it.';
      lead.hidden = state === 'success';
      const content = this.querySelector('.eyesnug-mystery-offer__content');
      if (content) content.scrollTop = 0;
      if (changed && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.stepAnimation?.cancel();
        this.stepAnimation = this.querySelector('.eyesnug-mystery-offer__inner')?.animate?.(
          [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 180, easing: 'ease-out' }
        );
      }
      if (state === 'choose') return;
      const offer = this.config();
      this.querySelector('[data-offer-label]').textContent = offer.label;
      this.querySelector('[data-mystery-submit]').textContent = `Claim my ${offer.label}`;
      this.querySelector('[data-mystery-tags]').value = `newsletter,eyesnug_mystery_splash,${offer.tag}`;
      if (state === 'success') {
        this.querySelector('[data-result-code]').textContent = offer.code;
        const link = this.querySelector('[data-result-link]');
        const root = window.Shopify?.routes?.root || '/';
        link.href = `${root}discount/${encodeURIComponent(offer.code)}?redirect=${encodeURIComponent(this.currentPagePath())}`;
        link.textContent = `Shop with ${offer.label}`;
      }
    }
    currentPagePath() {
      const page = new URL(window.location.href);
      // Shopify adds these after a signup; do not replay them when applying the code.
      page.searchParams.delete('customer_posted');
      if (page.hash === `#${this.form?.id}`) page.hash = '';
      return `${page.pathname}${page.search}${page.hash}`;
    }
    prepareSubmission(event) {
      if (this.nativeSubmission) return;
      if (this.state !== 'claim' || !this.pendingOutcome() || this.claiming) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
        return;
      }
      this.syncEmail();
      this.querySelector('[data-mystery-tags]').value = `newsletter,eyesnug_mystery_splash,${this.config().tag}`;
      let returnTo = this.form.querySelector('[name="return_to"]');
      if (!returnTo) {
        returnTo = document.createElement('input');
        returnTo.type = 'hidden';
        returnTo.name = 'return_to';
        this.form.append(returnTo);
      }
      // Preserve Shopify's success marker for both asynchronous and native responses.
      const successPage = new URL(this.currentPagePath(), window.location.href);
      successPage.searchParams.set('customer_posted', 'true');
      returnTo.value = `${successPage.pathname}${successPage.search}${successPage.hash}`;
      this.write('submitted', String(Date.now()), 1);
      if (!window.fetch || !window.FormData) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.claiming = true;
      const button = this.querySelector('[data-mystery-submit]');
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      button.textContent = 'Claiming your offer…';
      this.querySelector('[data-claim-error]').hidden = true;
      if (window.Shopify?.captcha?.protect) {
        // Use Shopify's supported integration, never its underlying CAPTCHA library.
        this.claimTimer = window.setTimeout(() => this.claimError('We could not prepare your signup. Please try again.'), 20000);
        try {
          window.Shopify.captcha.protect(this.form, () => {
            if (!this.claiming) return;
            const token = this.form.querySelector('[name="h-captcha-response"]')?.value;
            if (token) this.submitClaim();
            else this.submitNatively();
          });
        } catch (_) {
          this.claimError('We could not prepare your signup. Please try again.');
        }
      } else {
        // Let Shopify prepare verification through its normal form submission.
        this.submitNatively();
      }
    }
    submitNatively() {
      window.clearTimeout(this.claimTimer);
      this.nativeSubmission = true;
      this.form.submit();
    }
    finishClaimRequest() {
      window.clearTimeout(this.claimTimer);
      this.claiming = false;
      const button = this.querySelector('[data-mystery-submit]');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      button.textContent = `Claim my ${this.config().label}`;
    }
    claimError(message) {
      this.finishClaimRequest();
      const error = this.querySelector('[data-claim-error]');
      error.textContent = message;
      error.hidden = false;
    }
    async submitClaim() {
      window.clearTimeout(this.claimTimer);
      const request = new AbortController();
      this.claimRequest = request;
      this.claimTimer = window.setTimeout(() => request.abort(), 20000);
      try {
        this.syncEmail();
        const response = await window.fetch(this.form.action, {
          method: 'POST', credentials: 'same-origin', signal: request.signal,
          headers: { Accept: 'text/html', 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(new FormData(this.form)).toString()
        });
        if ([400, 403].includes(response.status) || /\/challenge(?:\/|$)/.test(new URL(response.url).pathname)) {
          // Shopify can reject background requests before redirecting to its challenge.
          // Native submission retains form persistence and performs verification.
          this.finishClaimRequest();
          this.submitNatively();
          return;
        }
        if (!response.ok) throw new Error('signup_unavailable');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const server = page.getElementById(this.form.id)?.querySelector('[data-mystery-server-state]');
        if (server?.dataset.postedSuccess === 'true') {
          this.finishClaimRequest();
          this.completeClaim();
          return;
        }
        const error = page.querySelector('[data-claim-error]')?.textContent.trim();
        this.claimError(error || 'We could not confirm your signup. Please check your email and try again.');
      } catch (_) {
        this.claimError('We could not confirm your signup. Please try again.');
      } finally {
        window.clearTimeout(this.claimTimer);
        this.claimRequest = null;
      }
    }
    completeClaim() {
      this.write('completed', '1', 365);
      this.write('submitted', '0', 1);
      this.write('cart-apply-pending', String(Date.now()), 1);
      this.hidePrompts();
      this.email?.blur();
      this.setState('success');
      if (!this.dialog.hidden) this.heading.focus({ preventScroll: true });
      this.applyDiscount();
    }
    syncEmail() {
      const payload = this.querySelector('[data-mystery-email-payload]');
      if (this.email && payload) payload.value = this.email.value.trim();
    }
    async applyDiscount(event) {
      if (this.state !== 'success') return;
      if (event) {
        if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
      }
      if (this.applyingDiscount) return;
      if (this.discountApplied) {
        if (event) this.close();
        return;
      }
      const button = event?.currentTarget || this.querySelector('[data-result-link]');
      const label = button.textContent;
      let status = this.querySelector('[data-discount-status]');
      if (!status) {
        status = document.createElement('p');
        status.className = 'eyesnug-mystery-offer__error';
        status.setAttribute('role', 'alert');
        status.setAttribute('data-discount-status', '');
        this.querySelector('[data-mystery-result]').append(status);
      }
      status.hidden = true;
      this.applyingDiscount = true;
      this.automaticDiscountRequest = !event;
      this.discountRequest = new AbortController();
      const request = this.discountRequest;
      const timeout = window.setTimeout(() => request.abort(), 15000);
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      button.textContent = 'Applying your discount…';
      try {
        const root = window.Shopify?.routes?.root || '/';
        const options = { credentials: 'same-origin', signal: request.signal, headers: { Accept: 'application/json' } };
        const beforeResponse = await window.fetch(`${root}cart.js`, options);
        if (!beforeResponse.ok) throw new Error('cart_unavailable');
        const before = await beforeResponse.json();
        const code = this.config().code;
        // Retain other entered codes; Shopify decides which offers can combine.
        const codes = (before.discount_codes || []).map(discount => discount.code);
        if (!codes.some(existing => existing.toLowerCase() === code.toLowerCase())) codes.push(code);
        const drawer = document.querySelector('cart-drawer .drawer__inner');
        const response = await window.fetch(`${root}cart/update.js`, {
          ...options, method: 'POST',
          headers: { ...options.headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            discount: codes.join(','),
            ...(drawer ? { sections: ['cart-drawer'], sections_url: this.currentPagePath().split('#')[0] } : {})
          })
        });
        if (!response.ok) throw new Error('cart_unavailable');
        const cart = await response.json();
        const accepted = cart.discount_codes?.find(discount => discount.code.toLowerCase() === code.toLowerCase());
        if (accepted) this.write('cart-apply-pending', '0', 1);
        // Empty baskets retain the code until an eligible item is added.
        if (!accepted || (cart.item_count > 0 && accepted.applicable !== true)) {
          status.setAttribute('role', 'alert');
          status.textContent = 'This offer could not be applied to your current basket. You can copy the code above and check it at checkout.';
          status.hidden = false;
          return;
        }
        if (request.signal.aborted) return;
        this.discountApplied = true;
        // Refresh the existing drawer without opening it or stealing confirmation focus.
        if (drawer && cart.sections?.['cart-drawer']) {
          const updated = new DOMParser().parseFromString(cart.sections['cart-drawer'], 'text/html').querySelector('cart-drawer .drawer__inner');
          if (updated) drawer.innerHTML = updated.innerHTML;
        }
        if (event) {
          this.close();
        } else {
          // A successful email claim applies the offer without dismissing its confirmation.
          status.setAttribute('role', 'status');
          status.textContent = cart.item_count > 0
            ? 'Your discount is applied to your basket.'
            : 'Your discount is saved. It will apply when you add eligible items to your basket.';
          status.hidden = false;
        }
      } catch (_) {
        status.setAttribute('role', 'alert');
        status.textContent = 'We could not confirm your discount. Please try again, or copy the code above to use at checkout.';
        status.hidden = false;
      } finally {
        window.clearTimeout(timeout);
        this.discountRequest = null;
        this.applyingDiscount = false;
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        button.textContent = label;
      }
    }
    async copyCode(button) {
      const code = this.querySelector('[data-result-code]');
      try {
        await navigator.clipboard.writeText(code.textContent.trim());
        button.textContent = 'Copied';
        window.setTimeout(() => { button.textContent = 'Copy code'; }, 1800);
      } catch (_) {
        const range = document.createRange(); range.selectNodeContents(code);
        const selection = window.getSelection(); selection.removeAllRanges(); selection.addRange(range);
      }
    }
    write(suffix, value, days) {
      this.memory.set(suffix, value);
      try { window.localStorage.setItem(`${this.storageKey}:${suffix}`, value); } catch (_) { /* Cookie + memory fallback. */ }
      try { document.cookie = `${this.cookieKey}_${suffix.replace(/-/g, '_')}=${encodeURIComponent(value)}; Max-Age=${days * 86400}; Path=/; SameSite=Lax; Secure`; } catch (_) { /* In-memory fallback. */ }
    }
    read(suffix) {
      if (this.memory.has(suffix)) return this.memory.get(suffix);
      let stored;
      try { stored = window.localStorage.getItem(`${this.storageKey}:${suffix}`); } catch (_) { /* Cookie fallback. */ }
      return stored || this.readCookie(`${this.cookieKey}_${suffix.replace(/-/g, '_')}`);
    }
    readCookie(name) {
      try {
        const item = document.cookie.split('; ').find(cookie => cookie.startsWith(`${name}=`));
        return item ? decodeURIComponent(item.slice(name.length + 1)) : null;
      } catch (_) { return null; }
    }
  }
  customElements.define('eyesnug-mystery-offer', EyesnugMysteryOffer);
  document.addEventListener('shopify:section:select', event => {
    const section = event.target.querySelector('eyesnug-mystery-offer');
    if (section) section.open();
  });
})();
